import { RedeemDisplayInfo, Reward, OrderWithContext, CustomerProfile, PurchasedReward } from '@/models';
import { Routes } from '@/router';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { loadScript } from '@/helpers';
import { AccountManagementEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';
import { toTitleCase } from '@/helpers/formatting';
import moment from 'moment';

@Component({
  name: 'CmgRewardRedemptionSuccessModal',
})

export default class CmgRewardRedemptionSuccessModal extends Vue {

  isLottieLibraryLoaded = false;

  get isUpdatedContextualRewardsBagUpdatesEnabled(): boolean {
    return this.$appStore.state.featureFlags.featureFlags.isUpdatedContextualRewardsBagUpdatesEnabled;
  }

  get purchasedReward(): PurchasedReward | undefined {
    return this.$appStore.state.ui.modals.rewardRedemptionSuccess.args?.purchasedReward;
  }

  get rewardExpirationDate(): string {
    return this.purchasedReward?.promotionExpirationDate
      ? moment(this.purchasedReward.promotionExpirationDate).format('M/DD/YYYY')
      : '';
  }

  get formattedRewardExpirationDate(): string | undefined {
    const promotionExpirationDate = this.purchasedReward?.promotionExpirationDate;
    if (promotionExpirationDate) {
      const expirationDate = new Date(promotionExpirationDate);

      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      if (expirationDate.toDateString() == today.toDateString()) {
        return 'today';
      } else if (expirationDate.toDateString() == tomorrow.toDateString()) {
        return 'tomorrow'
      } else {
        return expirationDate.toLocaleDateString('en-us', {
          weekday: 'long',
          month:'short',
          day: 'numeric'
        });
      }
    }
  }

  get container() {
    return document.getElementById('achievementLottie')!;
  }

  animationComplete = () => this.container.remove();

  get qrWidth(): number {
    return document.documentElement.clientWidth * 0.5;
  }

  async mounted() {
    await this.loadLottieJsLibrary();
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get showErrorMessage(): boolean {
    return !!this.$appStore.state.ui.modals.rewardRedemptionSuccess.args!.showErrorMessage;
  }

  get customGoodsTitle() {
    return this.customGoodsReward?.title;
  }

  get customGoodsReward(): RedeemDisplayInfo | undefined {
    return this.purchasedReward?.redeemDisplayInfo;
  }

  get customGoodsDescription() {
    return this.customGoodsReward?.description;
  }

  get customGoodsMediaUri() {
    return this.customGoodsReward?.mediaUri;
  }

  get dancingAvocadoVideoUrl() {
    return this.$appStore.state.cms.images.dancingAvocado.default;
  }

  setLoadingStatus(loading: boolean) {
    this.$services.ui.setAppBlur(loading);
    this.$services.ui.setAppPepperVisibility(loading);
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get currentOrder(): OrderWithContext | null {
    return this.$store.getters.currentOrder;
  }

  get lottieIntroSparkUrl(): string {
    return this.$appStore.state.cms.animations.bag.lottieIntroSparkUrl;
  }

  get lottieBackgroundSparklesUrl(): string {
    return this.$appStore.state.cms.animations.bag.lottieBackgroundSparklesUrl;
  }

  async loadLottieJsLibrary(): Promise<any> {
    if (!this.isLottieLibraryLoaded) {
      this.isLottieLibraryLoaded = true;
      return await Promise.all([
        loadScript('https://unpkg.com/@lottiefiles/lottie-interactivity@latest/dist/lottie-interactivity.min.js'),
        loadScript('https://unpkg.com/@lottiefiles/lottie-player@1.5.7/dist/lottie-player.js')
      ]);
    }
  }

  get happyAvocadoUrl(): string {
    return this.$appStore.state.cms.images.feedback.happyAvocado.default;
  }

  get sadAvocadoUrl(): string {
    return this.$appStore.state.cms.images.feedback.sadAvocado.default;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.rewardRedemptionSuccess.visible;
  }

  get reward(): Reward {
    return this.$appStore.state.ui.modals.rewardRedemptionSuccess.args!.reward;
  }

  get rewardImgUrl(): string {
    return this.reward.mediaUri;
  }

  get sparkleAnimation(): string {
    return this.$appStore.state.cms.images.icons.sparkle.default;
  }

  get rewardTitle(): string {
    return toTitleCase(this.reward.title);
  }

  get offerType(): string {
    return this.reward.offerType.toLowerCase();
  }

  get rewardPromoCode(): string {
    let promocode = this.purchasedReward?.promotionCode || '';
    this.$services.order.setPromoCode(promocode);
    return promocode;
  }

  onClickRewardsExchange() {
    this.$router.push({ name: Routes.RewardsExchange });
    this.onClose();
  }

  onClickDone() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardDetailModal,
      action: AnalyticsEventAction.DoneSuccessMessageClicked,
      label: AnalyticsEventLabel.DoneSuccessMessageClicked,
    });
    this.onClose();
  }

  async onClickAddToBag() {
    this.$services.analytics.send(new AccountManagementEvent(
      InteractionTrackingType.RewardsAddedToBag,
      undefined,
      this.reward.title,
      'Reward Redemption Success',
      undefined,
      undefined,
      undefined,
      undefined,
      'order:cart',
    ));

    this.$services.reward.addPromotionToBag(undefined, this.purchasedReward,
      () => { this.onClose(); }, 
      (loading: boolean) => { this.setLoadingStatus(loading) });
  }

  onClickViewRewards() {
    if (this.showErrorMessage) {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.RewardDetailModal,
        action: AnalyticsEventAction.RedemptionPurchaseError,
        label: AnalyticsEventLabel.RedemptionPurchaseError,
      });
    } else {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.RewardDetailModal,
        action: AnalyticsEventAction.ViewInMyRewardsClick,
        label: AnalyticsEventLabel.ViewInMyRewardsClick,
      });
    }

    this.$router.push({ name: Routes.Rewards, hash: '#rewards' });
    this.onClose();
  }

  get savedState() {
    return {
      redeemDisplayInfo: this.customGoodsReward,
      promotionCode: this.rewardPromoCode,
      reward: this.reward,
      purchasedReward: this.purchasedReward
    }
  }

  onClickTermsAndConditions(event: any) {
    const reward = this.reward;
    const savedState = Object.assign({}, this.savedState);
    const onBack = () => {
      this.$services.ui.setModalVisibility('rewardRedemptionSuccess', true, savedState);
      this.$services.ui.setModalVisibility('rewardTermsAndConditions', false);
    };

    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    const terms = this.customGoodsReward ? this.customGoodsReward.terms : reward.terms;
    this.$services.ui.setModalVisibility('rewardTermsAndConditions', true, { termsAndConditions: this.purchasedReward?.promotionTerms, onBack });
    this.$services.ui.firstFocus();
    this.$services.ui.setModalVisibility('rewardRedemptionSuccess', false, savedState);
  }

  onClose() {
    this.$services.ui.setModalVisibility('rewardRedemptionSuccess', false);
    this.$services.ui.setModalVisibility('account', false);
  }
}